import React, { useEffect, useState } from "react";
import AddinInfo from "./AddinInfo";
import {
  getSubscriptionSettings,
  updateSubscriptionSettings,
} from "../../services/archiveApi";

function formatExpiry(value) {
  if (!value) return "Not active";

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "Unknown";

  return parsed.toLocaleString();
}

export default function SettingsTab({ version, environment, accountEmail }) {
  const [settings, setSettings] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [feedback, setFeedback] = useState(null);

  useEffect(() => {
    let isMounted = true;

    async function loadSettings() {
      try {
        setIsLoading(true);
        const response = await getSubscriptionSettings(accountEmail);

        if (isMounted) {
          setSettings(response);
        }
      } catch (error) {
        console.error("Subscription settings load failed:", error);
        if (isMounted) {
          setFeedback({
            type: "error",
            message: error?.message || "Could not load subscription settings.",
          });
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    }

    loadSettings();
    return () => {
      isMounted = false;
    };
  }, [accountEmail]);

  const handleToggle = async () => {
    if (!settings || isSaving) return;

    const nextEnabled = !settings.enabled;
    setIsSaving(true);
    setFeedback(null);

    try {
      const updated = await updateSubscriptionSettings({
        enabled: nextEnabled,
        userEmail: accountEmail,
      });

      setSettings(updated);
      setFeedback({
        type: "success",
        message: nextEnabled
          ? "Automatic email subscription is now enabled."
          : "Automatic email subscription is now disabled.",
      });
    } catch (error) {
      console.error("Subscription settings update failed:", error);
      setFeedback({
        type: "error",
        message: error?.message || "Could not update subscription setting.",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const isEnabled = Boolean(settings?.enabled);
  const statusLabel = isEnabled ? "Enabled" : "Disabled";

  return (
    <>
      {feedback ? (
        <div className={`status-banner ${feedback.type}`}>
          <div>{feedback.message}</div>
        </div>
      ) : null}

      <section className="card">
        <div className="card-header">
          <div className="card-header-dot" />
          <h3>Automatic Filing</h3>
        </div>
        <div className="card-body settings-stack">
          <div className="settings-hero">
            <div>
              <div className="setting-label">Microsoft subscription</div>
              <div className="setting-sublabel">
                New inbox emails will be filed automatically using the same backend process as manual filing.
              </div>
            </div>

            <button
              type="button"
              className={`toggle-switch ${isEnabled ? "enabled" : ""}`}
              onClick={handleToggle}
              disabled={isLoading || isSaving || !settings}
              aria-pressed={isEnabled}
              aria-label={`Turn automatic email filing ${isEnabled ? "off" : "on"}`}
            >
              <span className="toggle-track">
                <span className="toggle-thumb" />
              </span>
              <span className="toggle-label">
                {isSaving ? "Saving..." : statusLabel}
              </span>
            </button>
          </div>

          <div className="setting-row">
            <div>
              <div className="setting-label">Subscription status</div>
              <div className="setting-sublabel">
                {settings?.active ? "Graph subscription is active." : "No active Graph subscription."}
              </div>
            </div>
            <span className={`status-pill ${settings?.active ? "online" : "offline"}`}>
              {settings?.active ? "Active" : "Inactive"}
            </span>
          </div>

          <div className="setting-row">
            <div>
              <div className="setting-label">Renewal / expiry</div>
              <div className="setting-sublabel">
                Backend will renew automatically before expiration while this setting stays on.
              </div>
            </div>
            <span className="version-badge">{formatExpiry(settings?.expirationDateTime)}</span>
          </div>

          {settings?.lastError ? (
            <div className="settings-note error-note">
              Last backend error: {settings.lastError}
            </div>
          ) : null}
        </div>
      </section>

      <AddinInfo version={version} environment={environment} accountEmail={accountEmail} />
    </>
  );
}
